import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiMessageCircle, FiShoppingBag, FiHeart, FiStar, FiArrowRight } = FiIcons;

const HomePage = () => {
  const { user, profile } = useSupabaseAuth();
  
  // Dynamic link for "Become a Seller"
  const getSellerLink = () => {
    if (!user) return "/register?role=seller";
    if (profile?.is_seller) return "/seller-dashboard";
    return "/profile#become-seller";
  };

  const features = [
    { icon: FiSearch, title: 'Discover Local', description: 'Find talented cooks and specialty makers in your neighborhood.' },
    { icon: FiMessageCircle, title: 'Direct Chat', description: 'Connect instantly to discuss custom orders and dietary needs.' },
    { icon: FiShoppingBag, title: 'Easy Ordering', description: 'Coordinate local pickup or delivery directly with your neighbors.' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-slate-50 py-24 md:py-32 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none">
          <div className="absolute top-20 left-10 w-64 h-64 bg-indigo-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-500 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="inline-flex items-center px-4 py-2 rounded-full bg-indigo-50 text-indigo-700 text-xs font-black uppercase tracking-[0.2em] mb-8"
            >
              <SafeIcon icon={FiHeart} className="mr-2" /> Community First Marketplace
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl md:text-8xl font-black text-slate-900 mb-8 tracking-tighter leading-[0.9]"
            >
              Authentic Food <br /> <span className="text-indigo-600">Made by Neighbors</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-slate-500 mb-12 max-w-2xl mx-auto font-medium"
            >
              SohLocal connects you with talented home cooks and artisans. Discover fresh, authentic creations prepared right in your community.
            </motion.p>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-5 justify-center"
            >
              <Link to="/sellers" className="bg-slate-900 text-white px-10 py-5 rounded-2xl text-lg font-black hover:bg-slate-800 transition-all flex items-center justify-center shadow-2xl shadow-slate-200 transform hover:-translate-y-1">
                Explore Marketplace <SafeIcon icon={FiArrowRight} className="ml-3" />
              </Link>
              <Link to={getSellerLink()} className="bg-white text-slate-900 border border-slate-200 px-10 py-5 rounded-2xl text-lg font-black hover:bg-slate-50 transition-all flex items-center justify-center shadow-sm transform hover:-translate-y-1">
                Become a Seller
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-indigo-600 group-hover:text-white transition-all duration-300">
                  <SafeIcon icon={feature.icon} className="text-2xl" />
                </div>
                <h3 className="text-2xl font-black text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-500 leading-relaxed font-medium">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto bg-indigo-600 rounded-[3rem] p-12 md:p-24 text-center relative overflow-hidden shadow-2xl shadow-indigo-200">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-20 -mt-20 blur-3xl"></div>
          <div className="relative z-10">
            <h2 className="text-4xl md:text-6xl font-black text-white mb-8 tracking-tighter">Ready to Taste Local?</h2>
            <p className="text-xl text-indigo-100 mb-12 max-w-xl mx-auto font-medium">Join our growing community of food lovers and talented neighbor cooks today.</p>
            <Link to={getSellerLink()} className="bg-white text-indigo-600 px-12 py-5 rounded-2xl text-xl font-black hover:bg-slate-50 transition-all inline-flex items-center shadow-xl transform hover:scale-105">
              Get Started Now <SafeIcon icon={FiStar} className="ml-3" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;